import type { ReactNode } from 'react';
import { ImageIcon, ImageOff, Video, X } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { cn } from '@/lib/utils';
import { MEDIA_TONE_PRESETS, type Collaborator, type ContentMedia } from '@/client/types/marketing';

export function StatusBadge({ label, className }: { label: string; className: string }) {
  return (
    <span className={cn('inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium', className)}>
      {label}
    </span>
  );
}

export function CollaboratorAvatars({
  ids,
  collaborators,
  max = 4,
}: {
  ids: string[];
  collaborators: Collaborator[];
  max?: number;
}) {
  const people = ids
    .map((id) => collaborators.find((c) => c.id === id))
    .filter((c): c is Collaborator => !!c);

  if (people.length === 0) {
    return <span className="text-xs text-gray-400">—</span>;
  }

  const shown = people.slice(0, max);
  const overflow = people.length - shown.length;

  return (
    <div className="flex items-center -space-x-2">
      {shown.map((p) => (
        <div
          key={p.id}
          title={p.name}
          className={cn(
            'flex h-7 w-7 items-center justify-center rounded-full text-[11px] font-semibold text-white ring-2 ring-white',
            p.avatarColor,
          )}
        >
          {p.name
            .split(' ')
            .map((n) => n[0])
            .slice(0, 2)
            .join('')
            .toUpperCase()}
        </div>
      ))}
      {overflow > 0 && (
        <div className="flex h-7 w-7 items-center justify-center rounded-full bg-gray-100 text-[10px] font-semibold text-gray-500 ring-2 ring-white">
          +{overflow}
        </div>
      )}
    </div>
  );
}

export function EmptyState({ label }: { label: string }) {
  return (
    <div className="rounded-xl border border-dashed border-gray-200 bg-white py-14 text-center text-sm text-gray-400">
      {label}
    </div>
  );
}

/** `change_pct` is a fraction. Null is "new" only when the tile has a value. */
export function KpiChange({ change, value }: { change: number | null; value: number | null }) {
  if (value == null) return null;
  if (change == null) {
    return <span className="text-xs font-medium text-gray-400">new</span>;
  }
  const positive = change >= 0;
  return (
    <span
      className={cn(
        'flex items-center gap-1 text-xs font-medium',
        positive ? 'text-emerald-600' : 'text-red-600',
      )}
    >
      {positive ? '+' : ''}
      {(change * 100).toFixed(1)}%
    </span>
  );
}

export function DeleteConfirmDialog({
  open,
  title,
  description,
  onOpenChange,
  onConfirm,
}: {
  open: boolean;
  title: string;
  description: string;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
}) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm} className="bg-red-600 hover:bg-red-700">
            Delete
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export function BulkActionBar({
  count,
  onClear,
  children,
}: {
  count: number;
  onClear: () => void;
  children: React.ReactNode;
}) {
  if (count === 0) return null;
  return (
    <div className="mb-4 flex flex-wrap items-center gap-3 rounded-lg border border-blue-100 bg-blue-50 px-4 py-2.5 text-sm">
      <span className="font-medium text-blue-900">{count} selected</span>
      <div className="flex flex-wrap items-center gap-2">{children}</div>
      <button type="button" onClick={onClear} className="ml-auto text-xs font-medium text-blue-700 hover:underline">
        Clear
      </button>
    </div>
  );
}

export function MediaThumbnail({
  media,
  className,
  showLabel = true,
}: {
  media?: ContentMedia;
  className?: string;
  /** Hide the tone-preset label chip — turn off for small thumbnails where it can't fit cleanly. */
  showLabel?: boolean;
}) {
  if (media?.url) {
    return <img src={media.url} alt={media.alt ?? ''} className={cn('object-cover', className)} />;
  }
  if (media?.tone) {
    return (
      <div className={cn(media.tone, 'flex items-end overflow-hidden p-2', className)}>
        {showLabel && media.label && (
          <span className="truncate rounded bg-black/30 px-1.5 py-0.5 text-[10px] font-medium text-white">
            {media.label}
          </span>
        )}
      </div>
    );
  }
  return (
    <div className={cn('flex items-center justify-center bg-gray-100 text-gray-300', className)}>
      <ImageOff className="h-5 w-5" />
    </div>
  );
}

/** No real upload/storage — paste a URL or pick a placeholder tone. Mock-first, same as the rest of Marketing. */
export function MediaAttachmentField({
  value,
  onChange,
}: {
  value?: ContentMedia;
  onChange: (media: ContentMedia | undefined) => void;
}) {
  const set = (patch: Partial<ContentMedia>) => onChange({ kind: 'image', ...value, ...patch });

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-3">
        <MediaThumbnail media={value} className="h-16 w-16 shrink-0 rounded-lg" showLabel={false} />
        <div className="min-w-0 flex-1 space-y-2">
          <input
            value={value?.url ?? ''}
            onChange={(e) => set({ url: e.target.value || undefined })}
            placeholder="Paste an image URL…"
            className="h-9 w-full rounded-md border border-gray-200 bg-white px-3 text-sm outline-none ring-blue-500 focus:ring-2"
          />
          <div className="flex flex-wrap items-center gap-1.5">
            {MEDIA_TONE_PRESETS.map((preset) => (
              <button
                key={preset.tone}
                type="button"
                title={preset.label}
                onClick={() => set({ url: undefined, tone: preset.tone, label: preset.label })}
                className={cn(
                  'h-6 w-6 rounded-full border-2',
                  preset.tone,
                  value?.tone === preset.tone && !value.url ? 'border-gray-900' : 'border-transparent',
                )}
              />
            ))}
            <button
              type="button"
              onClick={() => set({ kind: value?.kind === 'video' ? 'image' : 'video' })}
              title="Toggle video"
              className={cn(
                'flex h-6 items-center gap-1 rounded-full border px-2 text-[10px] font-medium',
                value?.kind === 'video' ? 'border-gray-900 bg-gray-900 text-white' : 'border-gray-200 text-gray-500',
              )}
            >
              <Video className="h-3 w-3" /> Video
            </button>
            {value && (
              <button
                type="button"
                onClick={() => onChange(undefined)}
                className="flex h-6 items-center gap-1 rounded-full border border-gray-200 px-2 text-[10px] font-medium text-gray-500 hover:text-red-600"
              >
                <X className="h-3 w-3" /> Remove
              </button>
            )}
          </div>
        </div>
      </div>
      {!value?.url && !value?.tone && (
        <p className="flex items-center gap-1 text-[11px] text-gray-400">
          <ImageIcon className="h-3 w-3" /> No media attached — paste a URL or pick a placeholder tone.
        </p>
      )}
    </div>
  );
}

export function Panel({
  title,
  action,
  children,
  className = '',
}: {
  title: ReactNode;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('rounded-xl border border-gray-100 bg-white p-4 shadow-sm', className)}>
      <div className="mb-3 flex items-center justify-between gap-2">
        <h2 className="text-sm font-semibold text-gray-900">{title}</h2>
        {action}
      </div>
      {children}
    </div>
  );
}
